// Video Review — the approve/reject board for classroom cuts.
//
// Every episode in video_pipeline shows here with its poster, the actual
// video, and its production notes. Approving sets stage='published', which is
// what generate_classroom_manifest.py reads to decide whether a lesson appears
// on the site at all. Nothing reaches the public classroom without a click on
// this page.
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle2, RotateCcw, Play, ExternalLink, Clapperboard } from "lucide-react";

const sans = "'Instrument Sans', system-ui, -apple-system, sans-serif";
const serif = "'Instrument Serif', Georgia, serif";
const NAVY = "#002868", GOLD = "#C5A55A", RED = "#B22234";

interface Row {
  id: string;
  title: string | null;
  stage: string | null;
  video_url: string | null;
  poster_url: string | null;
  youtube_url: string | null;
  notes: string | null;
  sort_order: number | null;
  updated_at: string | null;
}

const SERIES = (id: string) =>
  id.startsWith("faq") ? "FAQ curriculum" : id.startsWith("fr") ? "Field Reports" : "Scenario Spotlights";

export default function VideoReviewManager() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [filter, setFilter] = useState<"pending" | "approved" | "all">("pending");
  const [openId, setOpenId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data } = await (supabase as any)
      .from("video_pipeline")
      .select("id,title,stage,video_url,poster_url,youtube_url,notes,sort_order,updated_at")
      .order("sort_order", { ascending: true });
    // Re-stamp every media URL with the row's own updated_at.
    //
    // The stored URLs already carry a ?v= param, but it is frozen at whatever
    // it was when the row was written. Re-rendering an episode usually reuses
    // the same filename, so that stale param made the browser (and the CDN
    // edge) serve last night's cut on a review board whose whole job is to
    // show the newest one. Stamping here means any DB touch busts the cache.
    const bust = (u: string | null, ts: string | null) => {
      if (!u) return u;
      const base = u.split("?")[0];
      const v = ts ? Date.parse(ts) : Date.now();
      return `${base}?v=${Number.isNaN(v) ? Date.now() : v}`;
    };
    setRows((data ?? [])
      .filter((r: Row) => r.video_url)
      .map((r: Row) => ({
        ...r,
        video_url: bust(r.video_url, r.updated_at),
        poster_url: bust(r.poster_url, r.updated_at),
      })));
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const setStage = async (id: string, stage: string) => {
    setBusy(id);
    await (supabase as any).from("video_pipeline").update({ stage, updated_at: new Date().toISOString() }).eq("id", id);
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, stage } : r)));
    setBusy(null);
  };

  const shown = useMemo(() => rows.filter((r) =>
    filter === "all" ? true : filter === "approved" ? r.stage === "published" : r.stage !== "published"
  ), [rows, filter]);

  const counts = useMemo(() => ({
    pending: rows.filter((r) => r.stage !== "published").length,
    approved: rows.filter((r) => r.stage === "published").length,
  }), [rows]);

  const btn = (active: boolean): React.CSSProperties => ({
    fontFamily: sans, fontSize: 13, fontWeight: 700, padding: "7px 14px", borderRadius: 8,
    cursor: "pointer", border: `1.5px solid ${active ? GOLD : "#ddd5c4"}`,
    background: active ? GOLD : "transparent", color: active ? "#0f1a2e" : "#3a3a3a",
  });

  return (
    <div style={{ fontFamily: sans }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
        <button style={btn(filter === "pending")} onClick={() => setFilter("pending")}>
          Needs review ({counts.pending})
        </button>
        <button style={btn(filter === "approved")} onClick={() => setFilter("approved")}>
          Approved &amp; live ({counts.approved})
        </button>
        <button style={btn(filter === "all")} onClick={() => setFilter("all")}>All ({rows.length})</button>
        <button style={{ ...btn(false), marginLeft: "auto" }} onClick={load}>Refresh</button>
      </div>

      <div style={{ background: "#fdf3d9", borderLeft: `4px solid ${GOLD}`, borderRadius: 6, padding: "10px 14px", fontSize: 13.5, marginBottom: 18 }}>
        Approving sets the episode live in the Classroom. Anything not approved shows as
        “Coming soon” with no player and no video URL in the site data — it can’t be linked or indexed.
        After approving, the manifest regenerates on the next publish run.
      </div>

      {loading && <div style={{ padding: 20 }}>Loading…</div>}
      {!loading && shown.length === 0 && (
        <div style={{ padding: 24, fontSize: 15, color: "#6b6250" }}>
          Nothing here — {filter === "pending" ? "everything has been reviewed." : "no approved episodes yet."}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {shown.map((r) => {
          const approved = r.stage === "published";
          const open = openId === r.id;
          return (
            <div key={r.id} style={{ background: "#fff", border: `1px solid ${approved ? "#bcd8bc" : "#ddd5c4"}`, borderLeft: `5px solid ${approved ? "#2f6b4f" : GOLD}`, borderRadius: 12, padding: 14 }}>
              <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
                <button onClick={() => setOpenId(open ? null : r.id)}
                  style={{ position: "relative", width: 210, flexShrink: 0, border: "none", padding: 0, background: "#0d1420", borderRadius: 8, overflow: "hidden", cursor: "pointer", aspectRatio: "16/9" }}>
                  {r.poster_url && <img src={r.poster_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                  <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ width: 46, height: 46, borderRadius: "50%", background: "rgba(197,165,90,.92)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Play size={20} color="#0f1a2e" style={{ marginLeft: 2 }} />
                    </span>
                  </span>
                </button>

                <div style={{ flex: "1 1 320px", minWidth: 260 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".07em", color: GOLD }}>
                    {SERIES(r.id)} · {r.id}
                    {approved && <span style={{ color: "#2f6b4f", marginLeft: 8 }}>● APPROVED &amp; LIVE</span>}
                  </div>
                  <div style={{ fontFamily: serif, fontSize: 21, color: NAVY, margin: "3px 0 5px" }}>{r.title ?? r.id}</div>
                  {r.notes && <div style={{ fontSize: 12.5, color: "#6b6250", lineHeight: 1.5, maxWidth: "78ch" }}>{r.notes}</div>}
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 8, fontSize: 12 }}>
                    <a href={r.video_url!} target="_blank" rel="noreferrer" style={{ color: "#6b6250", display: "inline-flex", alignItems: "center", gap: 4 }}>
                      <ExternalLink size={12} /> open file
                    </a>
                    {r.youtube_url && (
                      <a href={r.youtube_url} target="_blank" rel="noreferrer" style={{ color: "#6b6250", display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <ExternalLink size={12} /> YouTube
                      </a>
                    )}
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 150 }}>
                  {!approved ? (
                    <button disabled={busy === r.id} onClick={() => setStage(r.id, "published")}
                      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, fontSize: 13.5, fontWeight: 700, padding: "10px 14px", borderRadius: 9, border: "none", background: "#2f6b4f", color: "#fff", cursor: "pointer" }}>
                      <CheckCircle2 size={15} /> {busy === r.id ? "Saving…" : "Approve"}
                    </button>
                  ) : (
                    <button disabled={busy === r.id} onClick={() => setStage(r.id, "review")}
                      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, fontSize: 13.5, fontWeight: 700, padding: "10px 14px", borderRadius: 9, border: `1.5px solid ${RED}`, background: "transparent", color: RED, cursor: "pointer" }}>
                      <RotateCcw size={15} /> Pull back
                    </button>
                  )}
                  <button onClick={() => setOpenId(open ? null : r.id)}
                    style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, fontSize: 13, padding: "9px 14px", borderRadius: 9, border: "1.5px solid #ddd5c4", background: "transparent", color: "#3a3a3a", cursor: "pointer" }}>
                    <Clapperboard size={14} /> {open ? "Close" : "Watch"}
                  </button>
                </div>
              </div>

              {open && (
                <video controls autoPlay preload="metadata" src={r.video_url ?? undefined} poster={r.poster_url ?? undefined}
                  style={{ width: "100%", marginTop: 12, borderRadius: 10, background: "#000", aspectRatio: "16/9" }} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
